import {Preloader} from "components/Preloader";
import type {getPlayersJsonSuccess} from "modules/actions/players";
import {
	IMAGES_URL,
	MLB_PLAYER_AVATAR_IMAGES,
	MLB_PLAYER_FULL_IMAGES,
	MLB_PLAYER_SHOT_IMAGES,
} from "modules/constants";
import {PlayerImageType} from "modules/enums";
import React, {useEffect, useState} from "react";
import styled from "@emotion/styled";

const Position = styled.div`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	height: 100%;
	width: 100%;
`;

const ImageElement = styled.img`
	pointer-events: none;
`;

type TPlayer = ReturnType<typeof getPlayersJsonSuccess>["payload"]["players"][number];

const ONE_MINUTE = 60000;
const IMAGE_CACHE = Math.floor(Date.now() / ONE_MINUTE);

const getImageOrFallback = (path: string, fallback: string): Promise<string> => {
	return new Promise((resolve) => {
		const img = new Image();
		img.src = path;
		img.onload = () => resolve(path);
		img.onerror = () => resolve(fallback);
	});
};

const buildPlayerImageLink = (link: string, player: TPlayer) => {
	return link.replace("--player_feed_id--", `${player.feedId}`);
};

const getMLBOrOwnImage = (
	key: PlayerImageType,
	defaultImage: string,
	mlbLink: string,
	player: TPlayer
): Promise<string> => {
	const defaultLink = `${IMAGES_URL}image/${defaultImage}`;

	if (player[key]) {
		const link = `${IMAGES_URL}image/${String(player[key])}?_=${IMAGE_CACHE}`;

		return getImageOrFallback(link, defaultLink);
	}

	const mlbLinkToImage = `${buildPlayerImageLink(mlbLink, player)}?_=${IMAGE_CACHE}`;

	return getImageOrFallback(mlbLinkToImage, defaultLink);
};

const getPlayerAvatar = (player: TPlayer) => {
	return getMLBOrOwnImage(
		PlayerImageType.HEADSHOT,
		"player_headshot_default.png",
		MLB_PLAYER_AVATAR_IMAGES,
		player
	);
};

const getPlayerActionShotImage = (player: TPlayer) => {
	return getMLBOrOwnImage(
		PlayerImageType.ACTION_SHOT_IMAGE,
		"player_action_short_default.png",
		MLB_PLAYER_SHOT_IMAGES,
		player
	);
};

const getPlayerActionFullImage = (player: TPlayer): Promise<string> => {
	return getMLBOrOwnImage(
		PlayerImageType.ACTION_FULL_IMAGE,
		"player_action_full_default.png",
		MLB_PLAYER_FULL_IMAGES,
		player
	);
};

const functionByType = {
	[PlayerImageType.HEADSHOT]: getPlayerAvatar,
	[PlayerImageType.ACTION_SHOT_IMAGE]: getPlayerActionShotImage,
	[PlayerImageType.ACTION_FULL_IMAGE]: getPlayerActionFullImage,
};

const imageAltText = {
	[PlayerImageType.HEADSHOT]: "player's headshot",
	[PlayerImageType.ACTION_SHOT_IMAGE]: "player's action shot",
	[PlayerImageType.ACTION_FULL_IMAGE]: "player's action full",
};

interface IProps {
	type: PlayerImageType;
	player: TPlayer;
}

export const PlayerImageComponent: React.FC<IProps> = ({type, player}) => {
	const [link, setLink] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const altText = `${player.firstName} ${player.lastName} - ${imageAltText[type]}`;

	useEffect(() => {
		void functionByType[type](player)
			.then((url) => {
				setLink(url);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [player, type]);

	if (isLoading) {
		return (
			<Position>
				<Preloader noBackground={true} />
			</Position>
		);
	}

	if (!link) {
		return null;
	}

	return <ImageElement className={"player-image"} src={link} alt={altText} />;
};

export const PlayerImage = React.memo(PlayerImageComponent);